import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Filter, Plus } from '@saleshandy/icons';
// eslint-disable-next-line import/extensions
import { Button } from '@saleshandy/design-system';
import Input from '../../../../shared/design-system/components/input';
import EnrichmentFilterPopover from '../enrichment-filter-popover/enrichment-filter-popover';
import {
  EnrichmentStatus,
  EnrichmentType,
} from '../../enums/csv-enrichment.enums';

type EnrichmentToolbarProps = {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  statusFilter: EnrichmentStatus | null;
  typeFilter: EnrichmentType | null;
  onStatusFilterChange: (status: EnrichmentStatus | null) => void;
  onTypeFilterChange: (type: EnrichmentType | null) => void;
  onClearAllFilters: () => void;
  onNewEnrichment: () => void;
};

const EnrichmentToolbar: React.FC<EnrichmentToolbarProps> = ({
  searchQuery,
  onSearchChange,
  statusFilter,
  typeFilter,
  onStatusFilterChange,
  onTypeFilterChange,
  onClearAllFilters,
  onNewEnrichment,
}) => {
  const [searchValue, setSearchValue] = useState(searchQuery);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterAnchorRef = useRef<HTMLDivElement>(null);
  const hasActiveFilters = statusFilter !== null || typeFilter !== null;
  const filterCount =
    (statusFilter !== null ? 1 : 0) + (typeFilter !== null ? 1 : 0);

  useEffect(() => {
    setSearchValue(searchQuery);
  }, [searchQuery]);

  const clearSearch = () => {
    setSearchValue('');
    onSearchChange('');
  };

  const searchIcons = useMemo(() => {
    if (searchValue.trim().length === 0) {
      return [
        {
          place: Input.IconPlace.Left,
          identifier: 'search',
        },
      ];
    }

    return [
      {
        place: Input.IconPlace.Left,
        identifier: 'search',
      },
      {
        place: Input.IconPlace.Right,
        identifier: 'close-o',
        onClick: clearSearch,
      },
    ];
  }, [searchValue]);

  useEffect(() => {
    if (!isFilterOpen) return undefined;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const node = event.target as Node;
      if (filterAnchorRef.current?.contains(node)) return;
      const el = event.target as HTMLElement;
      if (el.closest?.('.bs-csv-enrichment__filter-popover')) return;
      setIsFilterOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isFilterOpen]);

  return (
    <div className="bs-csv-enrichment__toolbar">
      <div className="d-flex gap-12">
        <Input
          value={searchValue}
          onChange={(v) => setSearchValue(v)}
          onKeyPress={(e) => {
            if (e.key !== 'Enter') return;
            const trimmed = searchValue.trim();
            if (trimmed?.length === 0) {
              clearSearch();
              return;
            }
            if (searchValue !== searchQuery) {
              onSearchChange(searchValue);
            }
          }}
          placeholder="Search"
          icons={searchIcons as any}
        />

        <div
          ref={filterAnchorRef}
          id="enrichment-filter-popover-target"
          className="bs-csv-enrichment__filter-wrapper"
        >
          <button
            type="button"
            className={`bs-csv-enrichment__filter-btn ${
              isFilterOpen ? 'bs-csv-enrichment__filter-btn--active' : ''
            }`}
            onClick={() => setIsFilterOpen(!isFilterOpen)}
          >
            <Filter width={18} height={18} />
            {filterCount > 0 && (
              <span className="bs-csv-enrichment__filter-count-badge">
                {filterCount}
              </span>
            )}
          </button>

          <EnrichmentFilterPopover
            isOpen={isFilterOpen}
            target="enrichment-filter-popover-target"
            onClose={() => setIsFilterOpen(false)}
            statusFilter={statusFilter}
            typeFilter={typeFilter}
            onStatusFilterChange={onStatusFilterChange}
            onTypeFilterChange={onTypeFilterChange}
            onClearAllFilters={onClearAllFilters}
            hasActiveFilters={hasActiveFilters}
          />
        </div>
      </div>

      <Button onClick={onNewEnrichment} className="bs-csv-enrichment__new-btn">
        <Plus width={14} height={14} />
        New Enrichment
      </Button>
    </div>
  );
};

export default EnrichmentToolbar;
